import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { CheckCircle2, X, XCircle } from 'lucide-react';
import { cn } from '../../lib/cn';
import { ToastContext, type ToastApi } from './toastContext';

/**
 * Toasts.
 *
 * These exist to replace `alert()`, which the first version used to report a
 * failed clipboard copy. `alert()` blocks the main thread, cannot be styled, and
 * on mobile Safari it is a modal the user has to dismiss before anything else
 * works — a hostile way to deliver "copy didn't work, try again".
 *
 * The container is an `aria-live="polite"` region, so a screen reader announces
 * the message without stealing focus. `polite` rather than `assertive`: nothing
 * announced here is urgent enough to interrupt what the user is reading.
 */

type ToastTone = 'success' | 'error';

interface Toast {
  id: number;
  message: string;
  tone: ToastTone;
}

const DURATION_MS = 3500;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(0);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const push = useCallback(
    (message: string, tone: ToastTone) => {
      const id = nextId.current++;
      setToasts((current) => [...current, { id, message, tone }]);
      setTimeout(() => dismiss(id), DURATION_MS);
    },
    [dismiss]
  );

  const api = useMemo<ToastApi>(
    () => ({
      success: (message) => push(message, 'success'),
      error: (message) => push(message, 'error'),
    }),
    [push]
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        aria-live="polite"
        aria-atomic="false"
        className="no-print pointer-events-none fixed inset-x-0 bottom-4 z-50 flex flex-col items-center gap-2 px-4"
      >
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={cn(
              'pointer-events-auto flex max-w-sm items-start gap-2.5 rounded-field border px-3.5 py-2.5 text-sm shadow-lifted',
              'animate-rise',
              toast.tone === 'success'
                ? 'border-success-200 bg-success-50 text-success-700 dark:border-success-600 dark:bg-success-950 dark:text-success-100'
                : 'border-critical-200 bg-critical-50 text-critical-700 dark:border-critical-600 dark:bg-critical-950 dark:text-critical-100'
            )}
          >
            <span aria-hidden="true" className="mt-0.5 [&>svg]:size-4">
              {toast.tone === 'success' ? <CheckCircle2 /> : <XCircle />}
            </span>
            <p className="flex-1">{toast.message}</p>
            <button
              type="button"
              onClick={() => dismiss(toast.id)}
              className="mt-0.5 rounded p-0.5 opacity-60 transition-opacity hover:opacity-100"
            >
              <span className="sr-only-live">Dismiss</span>
              <X aria-hidden="true" className="size-4" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
